import React, { useState, useEffect, useRef } from 'react';
import { useChat } from '../contexts/ChatContext';
import { useSarLabAuth } from '../contexts/SarLabAuthContext';
import { useLiveSupport } from '../contexts/LiveSupportContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageCircle,
  X,
  Send,
  Users,
  WifiOff,
  Minimize2,
  Shield,
  Trash2,
  Headphones,
  MessageSquare,
  ArrowLeft
} from 'lucide-react';
import './ChatWidget.css';
import ShinyText from './ShinyText';
import LiveSupportModal from './LiveSupportModal';
import LiveSupportChat from './LiveSupportChat';

const ChatWidget: React.FC = () => {
  const { currentPlayer } = useSarLabAuth();
  const {
    messages,
    onlineUsers,
    isConnected,
    unreadCount,
    sendMessage,
    deleteMessage,
    setTyping,
    typingUsers,
    markAsRead
  } = useChat();
  const { activeTicket } = useLiveSupport();

  const [isOpen, setIsOpen] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [activeView, setActiveView] = useState<'menu' | 'chat' | 'support-form' | 'support-chat'>('menu');
  const [isMinimized, setIsMinimized] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [isTyping, setIsTypingState] = useState(false);
  const [showOnlineUsers, setShowOnlineUsers] = useState(true);
  const [isBanned, setIsBanned] = useState(false);
  const [isTimedOut, setIsTimedOut] = useState(false);
  const [timeoutUntil, setTimeoutUntil] = useState<number | null>(null);
  const [fontSize, setFontSize] = useState(() => {
    const saved = localStorage.getItem('chatFontSize');
    return saved ? parseInt(saved) : 14;
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Aktif ticket varsa support chat'e yönlendir ve widget'ı aç
  useEffect(() => {
    if (activeTicket) {
      setActiveView('support-chat');
      // Sayfa yenilendiğinde aktif ticket varsa widget'ı otomatik aç
      if (!isOpen && !showMenu) {
        setIsOpen(true);
      }
    }
  }, [activeTicket, isOpen, showMenu]);

  // Mesajları otomatik scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Font boyutu değiştir
  const changeFontSize = (newSize: number) => {
    setFontSize(newSize);
    localStorage.setItem('chatFontSize', newSize.toString());
  };

  // Typing timeout
  useEffect(() => {
    if (isTyping) {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      typingTimeoutRef.current = setTimeout(() => {
        setIsTypingState(false);
        setTyping(false);
      }, 3000);
    }
  }, [isTyping, setTyping]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageText.trim() || !currentPlayer) return;

    if (isBanned) {
      alert('Hesabınız banlanmıştır. Mesaj gönderemezsiniz.');
      return;
    }

    if (isTimedOut) {
      const remainingTime = timeoutUntil ? Math.ceil((timeoutUntil - Date.now()) / 60000) : 0;
      alert(`Hesabınız timeout edilmiştir. ${remainingTime} dakika sonra mesaj gönderebilirsiniz.`);
      return;
    }

    await sendMessage(messageText);
    setMessageText('');
    setIsTypingState(false);
    setTyping(false);
  };

  // Chat açıldığında mesajları okundu olarak işaretle
  useEffect(() => {
    if (isOpen && activeView === 'chat') {
      markAsRead();
    }
  }, [isOpen, activeView, markAsRead]);

  // Ban ve timeout kontrolleri - SarLab API entegrasyonu için placeholder
  useEffect(() => {
    if (!currentPlayer) return;

    // TODO: SarLab ban/timeout API entegrasyonu
    // Şimdilik ban ve timeout kontrollerini devre dışı bırakıyoruz
    setIsBanned(false);
    setIsTimedOut(false);
    setTimeoutUntil(null);
  }, [currentPlayer]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMessageText(value);

    if (value.trim() && !isTyping) {
      setIsTypingState(true);
      setTyping(true);
    } else if (!value.trim() && isTyping) {
      setIsTypingState(false);
      setTyping(false);
    }
  };

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypingText = () => {
    if (typingUsers.length === 0) return '';
    if (typingUsers.length === 1) {
      const user = onlineUsers.find(u => u.userId === typingUsers[0]);
      return `${user?.username || 'Birisi'} yazıyor...`;
    }
    return `${typingUsers.length} kişi yazıyor...`;
  };

  const handleButtonClick = () => {
    if (isOpen) {
      setIsOpen(false);
      setShowMenu(false);
    } else {
      setShowMenu(true);
    }
  };

  const handleMenuSelect = (view: 'chat' | 'support-form') => {
    setShowMenu(false);
    setIsOpen(true);
    setActiveView(activeTicket ? 'support-chat' : view);
  };

  const handleBack = () => {
    if (activeTicket) {
      setActiveView('support-chat');
    } else {
      setIsOpen(false);
      setShowMenu(true);
    }
  };

  const handleSupportFormSuccess = () => {
    setActiveView('support-chat');
  };

  if (!currentPlayer) return null;

  // Chat bağlantı durumu kontrolü
  if (!isConnected) {
    return (
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        className="fixed bottom-6 right-6 z-20 w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 shadow-2xl transition-all duration-300 sm:bottom-6 bottom-20"
        title="Chat bağlantısı yok"
      >
        <div className="flex items-center justify-center">
          <WifiOff className="w-6 h-6 text-white" />
        </div>
      </motion.button>
    );
  }

  return (
    <>
      {/* Main Chat Button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={handleButtonClick}
        className={`fixed bottom-6 right-6 z-20 w-14 h-14 rounded-full shadow-2xl transition-all duration-300 sm:bottom-6 bottom-20 ${
          isConnected
            ? 'bg-primary-500 hover:bg-primary-600'
            : 'bg-red-500 hover:bg-red-600'
        }`}
      >
        <div className="flex items-center justify-center">
          <MessageCircle className="w-6 h-6 text-white" />
        </div>

        {/* Unread Messages Badge */}
        {unreadCount > 0 && !isOpen && !showMenu && (
          <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
            {unreadCount}
          </div>
        )}

        {/* Active Support Badge */}
        {activeTicket && !isOpen && !showMenu && (
          <div className="absolute -top-1 -left-1 w-4 h-4 bg-green-500 rounded-full animate-pulse" />
        )}
      </motion.button>

      {/* Floating Menu Buttons */}
      <AnimatePresence>
        {showMenu && !isOpen && (
          <>
            {/* Backdrop to close menu - must be first/below buttons */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowMenu(false)}
              className="fixed inset-0 z-[19]"
            />

            {/* Live Support Button */}
            <motion.button
              initial={{ scale: 0, y: 0 }}
              animate={{ scale: 1, y: -140 }}
              exit={{ scale: 0, y: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              onClick={() => handleMenuSelect('support-form')}
              className="fixed bottom-6 right-6 z-[21] w-14 h-14 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 shadow-2xl transition-all duration-300 sm:bottom-6 bottom-20 flex items-center justify-center group"
            >
              <Headphones className="w-6 h-6 text-white" />
              <span className="absolute right-16 bg-dark-800 text-white text-sm px-3 py-1 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                Canlı Destek
              </span>
              {activeTicket && (
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-500 rounded-full animate-pulse" />
              )}
            </motion.button>

            {/* Chat Button */}
            <motion.button
              initial={{ scale: 0, y: 0 }}
              animate={{ scale: 1, y: -70 }}
              exit={{ scale: 0, y: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25, delay: 0.05 }}
              onClick={() => handleMenuSelect('chat')}
              className="fixed bottom-6 right-6 z-[21] w-14 h-14 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 shadow-2xl transition-all duration-300 sm:bottom-6 bottom-20 flex items-center justify-center group"
            >
              <MessageSquare className="w-6 h-6 text-white" />
              <span className="absolute right-16 bg-dark-800 text-white text-sm px-3 py-1 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                Sohbet
              </span>
              {unreadCount > 0 && (
                <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {unreadCount}
                </div>
              )}
            </motion.button>
          </>
        )}
      </AnimatePresence>

      {/* Chat/Support Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ duration: 0.2 }}
            className={`fixed bottom-24 right-6 z-20 bg-dark-800/95 backdrop-blur-md border border-dark-700/50 rounded-2xl shadow-2xl flex flex-col ${
              isMinimized
                ? 'w-80 h-16'
                : 'w-96 h-[500px] sm:h-[500px] h-[400px] sm:w-96 w-[calc(100vw-3rem)] sm:right-6 right-3'
            }`}
          >
            {/* Support Form View */}
            {activeView === 'support-form' && !activeTicket && (
              <LiveSupportModal
                onClose={() => {
                  setIsOpen(false);
                  setShowMenu(false);
                }}
                onSuccess={handleSupportFormSuccess}
                onBack={handleBack}
              />
            )}

            {/* Support Chat View */}
            {(activeView === 'support-chat' || activeTicket) && activeTicket && (
              <LiveSupportChat
                onClose={() => {
                  setIsOpen(false);
                  setShowMenu(false);
                }}
              />
            )}

            {/* Regular Chat View */}
            {activeView === 'chat' && !activeTicket && (
              <>
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-dark-700/50">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={handleBack}
                      className="p-1 hover:bg-dark-700/50 rounded transition-colors duration-200"
                    >
                      <ArrowLeft className="w-4 h-4 text-gray-400" />
                    </button>
                    <div className="flex items-center space-x-2">
                      <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
                      <span className="text-sm font-medium text-white">Sohbet</span>
                    </div>
                    {onlineUsers.length > 0 && (
                      <div className="flex items-center space-x-1 text-xs text-gray-400">
                        <Users className="w-3 h-3" />
                        <span>{onlineUsers.length}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    <div className="flex items-center space-x-1 bg-dark-700/50 rounded px-2 py-1">
                      <button
                        onClick={() => changeFontSize(Math.max(10, fontSize - 2))}
                        className="text-gray-400 hover:text-white transition-colors text-xs"
                      >
                        A-
                      </button>
                      <span className="text-xs text-white min-w-[1.5rem] text-center">{fontSize}</span>
                      <button
                        onClick={() => changeFontSize(Math.min(24, fontSize + 2))}
                        className="text-gray-400 hover:text-white transition-colors text-xs"
                      >
                        A+
                      </button>
                    </div>

                    <button
                      onClick={() => setShowOnlineUsers(!showOnlineUsers)}
                      className="p-1 hover:bg-dark-700/50 rounded transition-colors duration-200"
                    >
                      <Users className="w-4 h-4 text-gray-400" />
                    </button>
                    <button
                      onClick={() => setIsMinimized(!isMinimized)}
                      className="p-1 hover:bg-dark-700/50 rounded transition-colors duration-200"
                    >
                      <Minimize2 className="w-4 h-4 text-gray-400" />
                    </button>
                    <button
                      onClick={() => {
                        setIsOpen(false);
                        setShowMenu(false);
                      }}
                      className="p-1 hover:bg-dark-700/50 rounded transition-colors duration-200"
                    >
                      <X className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                </div>

                {/* Ban/Timeout Warning */}
                {(isBanned || isTimedOut) && !isMinimized && (
                  <div className={`px-4 py-2 text-sm ${
                    isBanned
                      ? 'bg-red-900/30 border-b border-red-700/50 text-red-300'
                      : 'bg-yellow-900/30 border-b border-yellow-700/50 text-yellow-300'
                  }`}>
                    {isBanned ? (
                      <div className="flex items-center space-x-2">
                        <Shield className="w-4 h-4" />
                        <span>Hesabınız banlanmıştır. Mesaj gönderemezsiniz.</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <Shield className="w-4 h-4" />
                        <span>
                          Hesabınız timeout edilmiştir.
                          {timeoutUntil && (
                            <span className="ml-1">
                              {Math.ceil((timeoutUntil - Date.now()) / 60000)} dakika sonra mesaj gönderebilirsiniz.
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {!isMinimized && (
                  <>
                    {/* Online Users */}
                    <AnimatePresence>
                      {showOnlineUsers && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="border-b border-dark-700/50 p-3"
                        >
                          <div className="text-xs text-gray-400 mb-2">
                            Çevrimiçi Kullanıcılar ({onlineUsers.length})
                          </div>
                          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                            {onlineUsers.map((user) => (
                              <motion.div
                                key={user.userId}
                                className="flex items-center space-x-2 px-3 py-2 bg-dark-700/50 rounded-lg flex-shrink-0 hover:bg-dark-600/50 transition-colors duration-200"
                              >
                                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                                <span className="text-xs text-white font-medium">{user.username}</span>
                              </motion.div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Messages */}
                    <div
                      className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-hide max-h-[300px] sm:max-h-none"
                      style={{ fontSize: `${fontSize}px` }}
                    >
                      {messages.length === 0 ? (
                        <div className="text-center text-gray-400 text-sm py-8">
                          Henüz mesaj yok. İlk mesajı siz gönderin!
                        </div>
                      ) : (
                        messages.map((message) => (
                          <div
                            key={message.id}
                            className={`flex ${
                              message.userId === currentPlayer?.id ? 'justify-end' : 'justify-start'
                            }`}
                          >
                            <div
                              className={`max-w-xs px-3 py-2 rounded-lg group ${
                                message.userId === currentPlayer?.id
                                  ? 'bg-primary-500 text-white'
                                  : 'bg-dark-700/50 text-gray-100'
                              }`}
                            >
                              <div className={`text-xs mb-1 flex items-center space-x-1 ${
                                message.userId === currentPlayer?.id ? 'text-black-200' : 'text-gray-400'
                              }`}>
                                {message.isAdmin && (
                                  <Shield className="w-4 h-4 text-Black-400 animate-pulse" />
                                )}
                                {message.isAdmin ? (
                                  <ShinyText
                                    text={message.username || 'Admin'}
                                    speed={7}
                                    variant="black-white"
                                    className="font-small text-s"
                                  />
                                ) : (
                                  <ShinyText
                                    text={message.username || 'Bilinmeyen Kullanıcı'}
                                    speed={5}
                                    className="font-small text-s"
                                  />
                                )}
                              </div>
                              <div className="text-sm">{message.text}</div>
                              <div className="flex items-center justify-between mt-1">
                                <div className="text-xs opacity-70">
                                  {formatTime(message.timestamp)}
                                </div>
                                {false && /* TODO: SarLab admin role check */ (
                                  <button
                                    onClick={() => deleteMessage(message.id)}
                                    className="opacity-0 group-hover:opacity-100 hover:bg-red-500/20 p-1 rounded transition-all duration-200"
                                    title="Mesajı Sil"
                                  >
                                    <Trash2 className="w-3 h-3 text-red-400 hover:text-red-300" />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}

                      {getTypingText() && (
                        <div className="text-xs text-gray-400 italic">
                          {getTypingText()}
                        </div>
                      )}

                      <div ref={messagesEndRef} />
                    </div>

                    {/* Message Input */}
                    <form onSubmit={handleSendMessage} className="p-4 border-t border-dark-700/50 flex-shrink-0">
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={messageText}
                          onChange={handleInputChange}
                          placeholder={isBanned ? "Hesabınız banlanmıştır" : isTimedOut ? "Hesabınız timeout edilmiştir" : "Mesajınızı yazın..."}
                          className={`flex-1 px-3 py-2 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm ${
                            isBanned || isTimedOut
                              ? 'bg-red-900/50 border-red-600/50 text-red-300'
                              : 'bg-dark-700/50 border-dark-600/50'
                          }`}
                          disabled={!isConnected || isBanned || isTimedOut}
                        />
                        <button
                          type="submit"
                          disabled={!messageText.trim() || !isConnected || isBanned || isTimedOut}
                          className="p-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      </div>
                    </form>
                  </>
                )}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
